import Cookies from 'universal-cookie';

const IDENTIFIER_COOKIE_KEY = 'fortellis_identifier_id';
const IDENTIFIER_STORAGE_KEY = 'fortellis_identifier_id';
const ONE_YEAR_IN_SECONDS = 365 * 24 * 60 * 60;

/**
 * Returns '.fortellis.io' if the current host is under fortellis.io, otherwise undefined (for localhost/dev).
 */
export const getCookieDomain = (
  hostname = typeof window !== 'undefined' ? window.location?.hostname : ''
) => {
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') {
    return undefined;
  }
  if (hostname === 'fortellis.io' || hostname.endsWith('.fortellis.io')) {
    return '.fortellis.io';
  }
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join('.')}`;
  }
  return undefined;
};

/**
 * Generates an RFC4122 v4 UUID.
 * Uses window.crypto.randomUUID() when available, with a random bitwise fallback.
 */
export const generateUUID = () => {
  if (
    typeof window !== 'undefined' &&
    window.crypto &&
    typeof window.crypto.randomUUID === 'function'
  ) {
    try {
      return window.crypto.randomUUID();
    } catch (e) {
      // Fall through to fallback
    }
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Retrieves the persistent identifier UUID from the root-domain cookie or localStorage,
 * or generates, saves, and syncs a new one across both cookie and localStorage.
 */
export const getOrGenerateIdentifierId = () => {
  try {
    const cookies = new Cookies();
    let identifierId = cookies.get(IDENTIFIER_COOKIE_KEY);

    // If not found in cookies, check localStorage
    if (!identifierId && typeof window !== 'undefined' && window.localStorage) {
      identifierId = window.localStorage.getItem(IDENTIFIER_STORAGE_KEY);
    }

    // If still not found, generate a new UUID
    if (!identifierId) {
      identifierId = generateUUID();
    }

    // Persist/sync to root cookie
    const domain = getCookieDomain();
    const isSecure =
      typeof window !== 'undefined' && window.location?.protocol === 'https:';

    cookies.set(IDENTIFIER_COOKIE_KEY, identifierId, {
      path: '/',
      maxAge: ONE_YEAR_IN_SECONDS,
      domain,
      secure: isSecure,
      sameSite: 'lax'
    });

    // Also sync to localStorage for redundancy
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(IDENTIFIER_STORAGE_KEY, identifierId);
    }

    return identifierId;
  } catch (e) {
    return generateUUID();
  }
};


/**
 * Retrieves the orgUnitId for the given entity or from the entities list by targetEntityId.
 * Returns null if not found or if orgUnitId is empty/non-string.
 */
export const getOrgUnitId = (entity, entities, targetEntityId) => {
  if (
    entity?.orgUnitId &&
    typeof entity.orgUnitId === 'string' &&
    entity.orgUnitId.trim() !== ''
  ) {
    return entity.orgUnitId.trim();
  }
  if (Array.isArray(entities) && targetEntityId) {
    const matched = entities.find(org => org?.id === targetEntityId);
    if (
      matched?.orgUnitId &&
      typeof matched.orgUnitId === 'string' &&
      matched.orgUnitId.trim() !== ''
    ) {
      return matched.orgUnitId.trim();
    }
  }
  return null;
};

/**
 * Extracts unique production and test versions for an API matching UI visibility rules.
 * Dev environment is excluded. Unauthenticated users only see General Release / null status prod versions and no test versions.
 * Returns { prodVersions: string[] | null, testVersions: string[] | null }
 */
export const getApiVersions = (data, isAuthenticated) => {
  if (!data) return { prodVersions: null, testVersions: null };

  const getInstancesForEnv = envNames => {
    if (Array.isArray(data.environments)) {
      const env = data.environments.find(e =>
        envNames.some(
          name => (e?.name || '').toLowerCase() === name.toLowerCase()
        )
      );
      return Array.isArray(env?.instances) ? env.instances : [];
    }
    if (Array.isArray(data.instances)) {
      return data.instances.filter(inst =>
        envNames.some(
          name =>
            (inst?.environment || '').toLowerCase() === name.toLowerCase()
        )
      );
    }
    return [];
  };

  let prodInstances = getInstancesForEnv(['prod', 'production']);
  let testInstances = getInstancesForEnv(['test']);

  // For unauthenticated users, match UI visibility (only General Release / null status for prod, no test)
  if (!isAuthenticated) {
    prodInstances = prodInstances.filter(
      inst =>
        inst.lifecycleStatus === 'General Release' ||
        inst.lifecycleStatus === null ||
        inst.lifecycleStatus === undefined
    );
    testInstances = [];
  }

  const formatVersions = instances => {
    const versions = Array.from(
      new Set(instances.map(inst => inst?.version).filter(Boolean))
    );
    return versions.length > 0 ? versions : null;
  };

  return {
    prodVersions: formatVersions(prodInstances),
    testVersions: formatVersions(testInstances)
  };
};

const toTitleCaseRole = role =>
  role
    .toLowerCase()
    .replace(/(^|[\s_-])[a-z]/g, match => match.toUpperCase());

/**
 * Normalizes user roles for GTM payloads.
 * Returns null when roles are missing/empty, otherwise returns title-cased roles.
 */
export const normalizeGtmUserRoles = roles => {
  if (!Array.isArray(roles)) {
    return null;
  }

  const normalizedRoles = roles
    .map(role => (typeof role === 'string' ? role.trim() : ''))
    .filter(Boolean)
    .map(toTitleCaseRole);

  return normalizedRoles.length > 0 ? normalizedRoles : null;
};

